"use client";

import { Check } from "lucide-react";
import Image from "next/image";
import { type CSSProperties, useRef } from "react";

import { TwinMark } from "@product/components/app/twin/twin-mark";

import { DATA_LABEL_CLS, DISPLAY } from "@/components/editorial";
import { centre, Cursor, Decision, enter, LoopStage, Marked, Packet, Panel, PILL, type Point, type Rect, SourceLabel, UiRoot, ui, useAnchors, useTicker } from "@/components/moments/ui";
import { useTimeline } from "@/components/moments/use-timeline";

import { type AskId, ASKS, DEFINITION, EXAMPLE_INVOICE, ON_YOUR_DATA, OVERDUE_CUSTOMERS, OVERDUE_TOTAL } from "./definition-story";

/* Steps: 0 rest, 1 the invoice and the proposed definition rise, 2 to 4 each
 * rule connects to the field it reads, 5 what the definition gives on the
 * workspace's data, 6 the actions rise and the pointer arrives, 7 Accept,
 * 8 the accepted definition takes the invoice's place, 9 the first answer
 * that used it, 10 the second, 11 the two figures marked as one, 12 out. */
const STEPS = [500, 800, 650, 650, 650, 1100, 1150, 450, 900, 1300, 1000, 3000, 450] as const;
const CARDS = 1;
const RULES = 2;
const DATA = 5;
const ACTIONS = 6;
const PRESS = 7;
const ACCEPTED = 8;
const ASKED = 9;
const SAME = 11;
const OUT = 12;

const TITLE = "mt-2.5 text-[19px] leading-tight font-semibold tracking-[-0.015em]";
const ROW = "flex justify-between gap-4 py-2 text-[13px]";
/** Under the invoice on a phone; beside it, across the threads' column, from 768px. */
const SECOND_CELL = "col-start-1 row-start-2 md:col-start-3 md:row-start-1";

/** How many of `total` have had their turn, `from` the step the first takes. */
const reached = (step: number, from: number, total: number) => Math.max(0, Math.min(total, step - from + 1));

/** The source record: one invoice as Xero holds it, each field the definition reads marked as its rule connects. */
function InvoiceCard({ connected }: { connected: number }) {
  return (
    <Panel anchor="invoice" className="p-5">
      <SourceLabel source="xero" kind="invoice" />
      <p style={DISPLAY} className={TITLE}>
        {EXAMPLE_INVOICE.number}
      </p>
      <dl className="mt-4 border-t border-[var(--ui-line)] pt-1">
        <div className={ROW}>
          <dt className="text-[var(--ui-quiet)]">Customer</dt>
          <dd className="font-medium">{EXAMPLE_INVOICE.customer}</dd>
        </div>
        {DEFINITION.rules.map((rule, index) => (
          <div key={rule.id} data-anchor={`field-${rule.id}`} className={ROW}>
            <dt className="text-[var(--ui-quiet)]">{rule.field}</dt>
            <dd className="font-medium">
              <Marked on={index < connected}>{EXAMPLE_INVOICE.fields[rule.id]}</Marked>
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/** Where the proposal stands: proposed, then accepted. */
function Standing({ accepted }: { accepted: boolean }) {
  return (
    <span className="grid shrink-0 justify-items-end text-[11.5px] font-medium">
      <span className="px-2 py-0.5 text-[var(--ui-quiet)] shadow-[inset_0_0_0_1px_var(--ui-line-strong)] [grid-area:1/1]" style={{ ...PILL, opacity: accepted ? 0 : 1, transition: "opacity 300ms" }}>
        Proposed
      </span>
      <span
        className="flex items-center gap-1 bg-[var(--ui-success-soft)] px-2 py-0.5 text-[var(--ui-success)] shadow-[inset_0_0_0_1px_var(--ui-success-line)] [grid-area:1/1]"
        style={{ ...PILL, opacity: accepted ? 1 : 0, transition: "opacity 400ms 200ms" }}
      >
        <Check aria-hidden strokeWidth={3} className="size-3" /> Accepted
      </span>
    </span>
  );
}

/** The proposal: the definition's name and its rules in plain words, then what it gives on the workspace's data. */
function DefinitionCard({ connected, counted, accepted }: { connected: number; counted: boolean; accepted: boolean }) {
  const overdue = useTicker(ON_YOUR_DATA.overdue, counted, 900);
  return (
    <Panel anchor="definition" lifted={accepted} className="p-5" style={{ transition: "box-shadow 500ms var(--ui-ease)" }}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[12px] text-[var(--ui-quiet)]">
          <TwinMark className="size-3.5" />
          <span>
            <span className="font-medium text-[var(--ui-ink)]">Definition</span> v{DEFINITION.version}
          </span>
        </span>
        <Standing accepted={accepted} />
      </div>
      <p data-anchor="definition-name" style={DISPLAY} className={`${TITLE} w-fit`}>
        {DEFINITION.name}
      </p>
      <ul className="m-0 mt-4 list-none border-t border-[var(--ui-line)] p-0 pt-1">
        {DEFINITION.rules.map((rule, index) => (
          <li key={rule.id} data-anchor={`rule-${rule.id}`} className="py-2 text-[13px] font-medium">
            <Marked on={index < connected}>{rule.rule}</Marked>
          </li>
        ))}
      </ul>
      <p className={`${ROW} mt-1 border-t border-[var(--ui-line)]`} style={enter(counted, { y: 4, duration: 500 })}>
        <span className="text-[var(--ui-quiet)]">On your data</span>
        <span className="font-medium tabular-nums">
          {Math.round(overdue)} of {ON_YOUR_DATA.open} open invoices
        </span>
      </p>
    </Panel>
  );
}

/** Someone else's assistant, by its mark. */
const TOOL_ICONS: Record<Exclude<AskId, "twin">, string> = { chatgpt: "/concepts/source-icons/chatgpt-mark.svg" };

/** The tool an answer was asked in: the Twin's own chat, or an assistant connected to it. */
function ToolMark({ id }: { id: AskId }) {
  if (id === "twin") return <TwinMark className="size-3.5" />;
  return <Image src={TOOL_ICONS[id]} alt="" width={14} height={14} className="shrink-0 object-contain" />;
}

/**
 * An answer that used the definition: where it was asked, the question, the
 * figure, and the definition and version it names. Once both answers are in,
 * their figures are marked: asked two ways, the same number.
 */
function AnswerCard({ index, shown, same }: { index: number; shown: boolean; same: boolean }) {
  const ask = ASKS[index]!;
  return (
    <div style={enter(shown)}>
      <Panel anchor={`answer-${index}`} className="px-5 py-3.5">
        <span className="flex items-center gap-1.5 text-[12px] text-[var(--ui-quiet)]">
          <ToolMark id={ask.id} />
          <span>
            Asked in <span className="font-medium text-[var(--ui-ink)]">{ask.tool}</span>
          </span>
        </span>
        <p className="mt-1.5 text-[13px]">“{ask.question}”</p>
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
          <span style={DISPLAY} className="text-[21px] leading-tight font-semibold tracking-[-0.02em] tabular-nums">
            <Marked on={same}>{OVERDUE_TOTAL}</Marked>
          </span>
          <span className="text-[12px] text-[var(--ui-quiet)]">
            {ON_YOUR_DATA.overdue} invoices, {OVERDUE_CUSTOMERS} customers
          </span>
        </p>
        <p data-anchor={`used-${index}`} className="mt-2 flex items-center gap-1.5 text-[12px] text-[var(--ui-quiet)]">
          <Check aria-hidden strokeWidth={3} className="size-3 shrink-0 text-[var(--ui-success)]" />
          <span>
            Used <span className="font-medium text-[var(--ui-ink)]">{DEFINITION.name}</span> v{DEFINITION.version}
          </span>
        </p>
      </Panel>
    </div>
  );
}

type Link = { from: Point; to: Point };
type Anchors = Readonly<Record<string, Rect>>;

/** A connector between two points: level where they are level, an S where they are not. */
function curve({ from, to }: Link): string {
  const mid = (from.x + to.x) / 2;
  return `M ${from.x} ${from.y} C ${mid} ${from.y}, ${mid} ${to.y}, ${to.x} ${to.y}`;
}

/** Each rule's connector: from the invoice's field, at the invoice's edge, to the rule that reads it. */
function ruleLinks(anchors: Anchors, invoice: Rect, definition: Rect): Link[] {
  return DEFINITION.rules.map((rule) => ({
    from: { x: invoice.x + invoice.width, y: centre(anchors[`field-${rule.id}`]).y },
    to: { x: definition.x, y: centre(anchors[`rule-${rule.id}`]).y },
  }));
}

/** Each answer's connector: from the accepted definition's name, where it now stands, to the line that names it. */
function answerLinks(anchors: Anchors, invoice: Rect, definition: Rect): Link[] {
  const from = { x: invoice.x + definition.width, y: centre(anchors["definition-name"]).y + invoice.y - definition.y };
  return ASKS.map((_, index) => ({ from, to: { x: anchors[`answer-${index}`]?.x ?? 0, y: centre(anchors[`used-${index}`]).y } }));
}

function Connector({ link }: { link: Link }) {
  return (
    <g>
      <path d={curve(link)} pathLength={1} className={ui.draw} fill="none" stroke="var(--ui-primary)" strokeOpacity={0.5} strokeWidth={1.5} />
      <circle cx={link.from.x} cy={link.from.y} r={3.5} fill="var(--ui-panel)" stroke="var(--ui-primary)" strokeWidth={1.5} />
      <circle cx={link.to.x} cy={link.to.y} r={3.5} fill="var(--ui-panel)" stroke="var(--ui-primary)" strokeWidth={1.5} />
    </g>
  );
}

/** The connectors, side by side only: the rules' while the proposal is open, then the answers', each carrying a packet. */
function Threads({ rules, answers, accepted }: { rules: readonly Link[]; answers: readonly Link[]; accepted: boolean }) {
  return (
    <div className="pointer-events-none absolute inset-0 hidden md:block">
      <svg className="absolute inset-0 h-full w-full overflow-visible">
        {/* Gone before the accepted definition has moved far across them. */}
        <g style={{ opacity: accepted ? 0 : 1, transition: "opacity 150ms" }}>
          {rules.map((link, index) => (
            <Connector key={DEFINITION.rules[index]!.id} link={link} />
          ))}
        </g>
        {answers.map((link, index) => (
          <Connector key={ASKS[index]!.id} link={link} />
        ))}
      </svg>
      {answers.map((link, index) => (
        <Packet key={ASKS[index]!.id} d={curve(link)} duration={900} delay={100} />
      ))}
    </div>
  );
}

/**
 * A definition agreed and reused, as motion (the home's, since 2026-10-02):
 * an invoice as Xero holds it, the definition your Twin proposes from its
 * fields, each rule drawn to the field it reads, what the definition gives
 * on the workspace's data, and a person accepting it. The accepted
 * definition then takes the invoice's place, and two answers asked in
 * different words in different tools rise beside it, with the same figure
 * and the definition's name. Decoration to assistive technology; the band
 * that stages it prints DEFINITION_SUMMARY.
 */
export function DefinitionEvidence() {
  const step = useTimeline(STEPS, { restStep: SAME });
  const stage = useRef<HTMLDivElement>(null);
  const anchors = useAnchors(stage);
  const invoice = anchors.invoice;
  const definition = anchors.definition;
  const accept = anchors.accept;
  const sideBySide = invoice && definition && definition.y <= invoice.y + 8;
  const accepted = step >= ACCEPTED;
  const drawn = reached(step, RULES, DEFINITION.rules.length);

  // Accepted, the definition takes the invoice's place and the answers take its own.
  const moved: CSSProperties = {
    transform: accepted && invoice && definition ? `translate(${invoice.x - definition.x}px, ${invoice.y - definition.y}px)` : "none",
    transition: step === 0 ? "none" : "transform 800ms var(--ui-ease)",
  };

  const start = accept ? { x: accept.x + accept.width + 150, y: accept.y + accept.height + 80 } : { x: 0, y: 0 };

  return (
    <UiRoot>
      <LoopStage out={step === OUT}>
        {/* data-nosnippet: the sample records never make a search result's snippet. */}
        <div
          ref={stage}
          aria-hidden="true"
          data-nosnippet=""
          className="relative mx-auto grid max-w-[64rem] gap-4 md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)] md:gap-0 lg:grid-cols-[minmax(0,1fr)_13rem_minmax(0,1fr)]"
        >
          {/* The definition and the answers share a cell at every width, so the stage keeps one height through both halves. */}
          <div className="col-start-1 row-start-1" style={{ opacity: accepted ? 0 : 1, transition: step === 0 ? "none" : "opacity 350ms var(--ui-ease)" }}>
            <div style={enter(step >= CARDS)}>
              <InvoiceCard connected={accepted ? 0 : drawn} />
            </div>
          </div>
          <div className={`relative z-10 ${SECOND_CELL}`} style={moved}>
            <div style={enter(step >= CARDS, { delay: 120 })}>
              <DefinitionCard connected={accepted ? 0 : drawn} counted={step >= DATA} accepted={accepted} />
            </div>
          </div>
          {/* On a phone the definition is a little taller than the invoice whose place it takes: the answers start clear of it. */}
          <div className={`grid content-start gap-3 pt-2 md:pt-0 ${SECOND_CELL}`}>
            {ASKS.map((ask, index) => (
              <AnswerCard key={ask.id} index={index} shown={step >= ASKED + index} same={step >= SAME} />
            ))}
          </div>

          {sideBySide ? (
            <Threads
              rules={ruleLinks(anchors, invoice, definition).slice(0, drawn)}
              answers={answerLinks(anchors, invoice, definition).slice(0, reached(step, ASKED, ASKS.length))}
              accepted={accepted}
            />
          ) : null}

          <Decision
            question="Use this definition?"
            shown={step >= ACTIONS}
            pressed={step === PRESS}
            made={accepted}
            className="col-start-1 row-start-3 mt-6 md:col-span-3 md:row-start-2 md:mt-8"
          />

          <Cursor at={step >= ACTIONS ? centre(accept) : start} down={step === PRESS} shown={step >= ACTIONS && step < ACCEPTED} />
        </div>
      </LoopStage>
    </UiRoot>
  );
}

/**
 * What the motion shows, as three plain points. Each is a claim the site
 * already makes: the home's hosting facts ("Checked answers show the
 * definition, version and sources they used") and the FAQ (a changed
 * definition is checked and released for review, with version history).
 */
export const DEFINITION_POINTS = [
  {
    title: "Drafted from your data",
    body: "Your Twin reads the systems you connect and drafts each definition in plain terms: what it counts, and what it leaves out.",
  },
  {
    title: "Accepted by you",
    body: "Each definition comes to your Inbox. Your Twin uses it for checked answers only once an admin has accepted it.",
  },
  {
    title: "Named in checked answers",
    body: "A checked answer shows the definition and version it used. A change is a new version, back in your Inbox for review.",
  },
] as const;

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

export function DefinitionPoints() {
  return (
    <ol className="m-0 grid list-none gap-x-10 gap-y-7 border-t border-dotted border-[var(--rule)] p-0 pt-8 md:grid-cols-3">
      {DEFINITION_POINTS.map((point, index) => (
        <li key={point.title}>
          <h3 style={DISPLAY} className={`flex items-baseline gap-2.5 text-[1.1rem] leading-[1.3] font-semibold ${INK_88}`}>
            <span className={`${DATA_LABEL_CLS} text-[var(--theme-accent-text)]`}>{String(index + 1).padStart(2, "0")}</span>
            {point.title}
          </h3>
          <p className={`mt-2 max-w-[46ch] text-[0.95rem] leading-[1.65] ${INK_72}`}>{point.body}</p>
        </li>
      ))}
    </ol>
  );
}
